import React, { FC, useContext, useEffect, useState } from 'react';
import { RcThemeInput } from '@ringcentral/juno';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { SEO, Sticky } from '@/components';
import {
  IProcessedProfile,
  ProfileBody,
  ProfileHeader,
} from '@/components/profileDetail';
import { ProfileViewModel } from '@/components/profileDetail/Profile.ViewModel';
import { useQueryParams } from '@/hooks';
import usePartnerHandlebars from '@/hooks/usePartnerHandlebars';
import { useVM } from '@/hooks/useVM';
import {
  buildHandlebarsProfile,
  directDownload,
  getAppPageTitle,
} from '@/pages/profile';
import {
  ExpiredPreviewLink,
  NavigationBar,
  ThemeOverrideContext,
} from '@/shared';
import { withLayout } from '@/shared/withLayout';
import { rootStore, useStores } from '@/stores';
import { AppDownloadLink } from '@/types';
import { BackToEditButton } from './backToEditButton';
import {
  MainContainer,
  navigationBarMixin,
  ProfileContainer,
} from './ProfilePreviewPage.sc';
import { resolveProfileForPreview } from './profilePreviewService';

interface ProfilePreviewPageProps {
  headerHeight?: number;
}

type IProfilePreviewState = IProcessedProfile | null | undefined;

const ProfilePreviewPagePure: FC<ProfilePreviewPageProps> = ({
  headerHeight,
}) => {
  const viewModel = useVM(ProfileViewModel);
  const { profile, setProfile } = viewModel;

  const [brandId, setBrandId] = useState<string>();
  const { profileId } = useParams<{ profileId: string }>();
  const { userStore } = useStores();
  const {
    t: timestamp,
    o: token,
    type,
    brandId: previewBrandId,
  } = useQueryParams();
  const themeOverride = useContext(ThemeOverrideContext);
  const handlebars = usePartnerHandlebars(brandId);

  const onDownload = (item: AppDownloadLink) => {
    if (profile) {
      directDownload(profile, item.link, userStore.isLoggedIn);
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (
      typeof timestamp !== 'string' ||
      typeof token !== 'string' ||
      typeof type !== 'string'
    ) {
      return;
    }

    resolveProfileForPreview(profileId, timestamp, token, type, previewBrandId)
      .then((profile) => {
        if (isMounted) {
          setBrandId(profile.brand);
          const handlebarsProfile = buildHandlebarsProfile(profile, handlebars);
          setProfile(handlebarsProfile);
        }
      })
      .catch(() => {
        if (isMounted) {
          setProfile(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [profileId, timestamp, token, type]);

  useEffect(() => {
    const currentTheme = resolveCurrentTheme(profile);

    if (currentTheme) {
      themeOverride.set(currentTheme);
    }

    return () => {
      themeOverride.reset();
    };
  }, [profile]);

  return (
    <MainContainer>
      {profile && (
        <React.Fragment>
          <SEO title={getAppPageTitle(profile)} />
          <Sticky position="top" offset={headerHeight}>
            <NavigationBar
              leftContent={<BackToEditButton />}
              cssProp={navigationBarMixin}
            />
          </Sticky>
          <ProfileContainer>
            <ProfileHeader
              data-test-automation-id="page-preview-header"
              viewModel={viewModel}
              onDownload={onDownload}
              isPreviewMode
            />
            <ProfileBody
              data-test-automation-id="page-preview-body"
              viewModel={viewModel}
              isPreviewMode
            />
          </ProfileContainer>
        </React.Fragment>
      )}
      {profile === null && <ExpiredPreviewLink />}
    </MainContainer>
  );
};

export const ProfilePreviewPage = withLayout(observer(ProfilePreviewPagePure), {
  stickyHeader: true,
});

const resolveCurrentTheme = (
  profile: IProfilePreviewState,
): RcThemeInput | undefined => {
  if (profile) {
    const brand = profile.brand;
    const compatibility =
      rootStore.compatibilitiesStore.getCompatibilityById(brand);
    return compatibility?.theme;
  }

  return undefined;
};
